export type BulkOutcome = {
  done: string[]
  failed: { id: string; reason: string }[]
  skipped: { id: string; reason: 'pending' }[]
}

/**
 * Run `op` over `items`, at most `concurrency` at a time (Q4: 4).
 *
 * A pool of workers each pulling the next index, rather than batches of four:
 * a batch waits for its slowest member, and one slow PATCH would idle three
 * lanes. One rejection never stops the rest — a bulk edit that gives up at the
 * first 500 leaves the user unable to tell what happened — and each rejection is
 * kept with the reason its `Error` carries.
 *
 * Aborting stops NEW starts only. The requests already in flight are not
 * cancelled, because each of them will land on the server either way and the
 * caller needs to know which. Items never started are in neither list.
 *
 * Progress counts handled items, failures included, so it reaches the total.
 */
export async function runBulk<T>(
  items: readonly T[],
  idOf: (t: T) => string,
  op: (t: T, signal: AbortSignal) => Promise<void>,
  opts: { concurrency: number; onProgress: (done: number, total: number) => void; signal?: AbortSignal },
): Promise<Omit<BulkOutcome, 'skipped'>> {
  const done: string[] = []
  const failed: { id: string; reason: string }[] = []
  const fallback = new AbortController()
  const signal = opts.signal ?? fallback.signal
  let next = 0
  let handled = 0

  async function worker() {
    while (!signal.aborted && next < items.length) {
      const item = items[next++]
      if (item === undefined) continue
      const id = idOf(item)
      try {
        await op(item, signal)
        done.push(id)
      } catch (cause) {
        failed.push({ id, reason: cause instanceof Error && cause.message !== '' ? cause.message : 'Failed' })
      }
      handled++
      opts.onProgress(handled, items.length)
    }
  }

  await Promise.all(Array.from({ length: Math.min(opts.concurrency, items.length) }, worker))
  return { done, failed }
}
